import React, { useState } from 'react';
import './Workflow.css';

function Workflow() {
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    {
      id: 'working',
      label: 'Working Directory',
      shortLabel: 'Working Dir',
      color: '#ef4444',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/>
          <polyline points="13 2 13 9 20 9"/>
        </svg>
      ),
      title: 'Working Directory (Directori de treball)',
      desc: "Es la carpeta del teu projecte on edites els fitxers. Git detecta automaticament quins fitxers han canviat, pero encara NO estan preparats per ser guardats.",
      analogy: "Es com el teu escriptori on tens papers esparsos. Pots modificar-los lliurement.",
      command: 'git status',
      output: 'On branch main\nChanges not staged for commit:\n  modified:  index.html\n  modified:  style.css\nUntracked files:\n  script.js',
    },
    {
      id: 'staging',
      label: 'Staging Area',
      shortLabel: 'Staging',
      color: '#f59e0b',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
          <line x1="12" y1="8" x2="12" y2="16"/>
          <line x1="8" y1="12" x2="16" y2="12"/>
        </svg>
      ),
      title: 'Staging Area (Area de preparacio)',
      desc: "Aqui selecciones quins canvis vols incloure al proper commit. Pots afegir tots els fitxers o nomes alguns.",
      analogy: "Es com posar papers dins d'un sobre. Tries quins papers vols enviar.",
      command: 'git add index.html style.css',
      output: 'Changes to be committed:\n  modified: index.html\n  modified: style.css\nUntracked files:\n  script.js',
    },
    {
      id: 'local',
      label: 'Local Repo',
      shortLabel: 'Local',
      color: '#22c55e',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="2" y="2" width="20" height="8" rx="2" ry="2"/>
          <rect x="2" y="14" width="20" height="8" rx="2" ry="2"/>
          <line x1="6" y1="6" x2="6.01" y2="6"/>
          <line x1="6" y1="18" x2="6.01" y2="18"/>
        </svg>
      ),
      title: 'Local Repository (Repositori local)',
      desc: "El repositori local (carpeta .git/) emmagatzema tot l'historial de commits. Un commit es una foto del teu projecte en un moment exacte. Es irreversible i te un identificador unic (hash).",
      analogy: "Es com tancar el sobre i arxivar-lo. Queda registrat amb data i autor.",
      command: 'git commit -m "afegir estils i estructura"',
      output: '[main a3f7b21] afegir estils i estructura\n 2 files changed, 45 insertions(+), 3 deletions(-)',
    },
    {
      id: 'remote',
      label: 'Remote Repo',
      shortLabel: 'Remote',
      color: '#3b82f6',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        </svg>
      ),
      title: 'Remote Repository (Repositori remot)',
      desc: "El repositori remot (GitHub, GitLab...) es on puges el teu codi perque estigui al nuvol. Aixi altres persones poden clonar-lo, i tu tens una copia de seguretat.",
      analogy: "Es com enviar el sobre per correu a una oficina central on tothom pot accedir-hi.",
      command: 'git push origin main',
      output: 'Enumerating objects: 5, done.\nCounting objects: 100% (5/5)\nWriting objects: 100% (3/3), 1.2 KiB\nTo github.com:user/repo.git\n   e9c4d18..a3f7b21  main -> main',
    },
  ];

  return (
    <section id="workflow" className="workflow">
      <div className="container">
        <div className="section-header">
          <span className="section-tag">Pas 3</span>
          <h2 className="section-title">Com funciona <span className="gold">Git</span>?</h2>
          <p className="section-desc">
            Git utilitza un flux de 4 arees. Entendre aquest flux es la clau de tot.
          </p>
        </div>

        <div className="workflow-diagram">
          <div className="workflow-steps">
            {steps.map((step, i) => (
              <React.Fragment key={step.id}>
                <button
                  className={`workflow-step ${activeStep === i ? 'active' : ''}`}
                  onClick={() => setActiveStep(i)}
                  style={{ '--step-color': step.color }}
                >
                  <div className="step-icon">{step.icon}</div>
                  <span className="step-label">{step.shortLabel}</span>
                </button>
                {i < steps.length - 1 && (
                  <div className="workflow-arrow">
                    <svg width="40" height="24" viewBox="0 0 40 24" fill="none">
                      <path d="M0 12h32M32 12l-8-6M32 12l-8 6" stroke={activeStep === i ? step.color : '#cdd1d7'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span className="arrow-label">
                      {i === 0 ? 'git add' : i === 1 ? 'git commit' : 'git push'}
                    </span>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="workflow-detail" style={{ '--step-color': steps[activeStep].color }}>
            <div className="detail-header">
              <div className="detail-icon">{steps[activeStep].icon}</div>
              <div>
                <h3>{steps[activeStep].title}</h3>
                <p>{steps[activeStep].desc}</p>
                <div className="detail-analogy">
                  <strong>Analogia:</strong> {steps[activeStep].analogy}
                </div>
              </div>
            </div>
            <div className="detail-terminal">
              <div className="terminal-header">
                <span className="dot red"></span>
                <span className="dot yellow"></span>
                <span className="dot green"></span>
              </div>
              <div className="terminal-body">
                <div className="terminal-line">
                  <span className="prompt">$</span>
                  <span className="cmd">{steps[activeStep].command}</span>
                </div>
                {steps[activeStep].output.split('\n').map((line, j) => (
                  <div className="terminal-line output" key={j}>{line}</div>
                ))}
              </div>
            </div>
          </div>

          {/* Reverse operations */}
          <div className="reverse-info">
            <h4>I a la inversa?</h4>
            <div className="reverse-grid">
              <div className="reverse-item">
                <code>git pull</code>
                <span>Remot &#8594; Local</span>
                <p>Descarrega i fusiona canvis del remot</p>
              </div>
              <div className="reverse-item">
                <code>git fetch</code>
                <span>Remot &#8594; Local (sense fusionar)</span>
                <p>Nomes descarrega, no modifica res</p>
              </div>
              <div className="reverse-item">
                <code>git restore &lt;file&gt;</code>
                <span>Descartar canvis locals</span>
                <p>Torna un fitxer a l'ultim commit</p>
              </div>
              <div className="reverse-item">
                <code>git restore --staged &lt;file&gt;</code>
                <span>Treure de staging</span>
                <p>Treu un fitxer de la staging area</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Workflow;
